import React, { useCallback, useMemo } from "react";
import { PrintItemElementDataEntity } from "../../../../../models/api/sift/back/api-elements";
import { ItemSettingsDataEntity } from "../../../../../models/api/sift/back/api-item-settings";
import { Checkbox } from "../../../../ui/input/checkbox";
import { Select } from "../../../../ui/select/select";
import { PrintOrderRequestParam } from "../hooks/use-create-print-order";
import { PrintLabeledUI } from "./print-labeled-ui";
import { PRINT_LAYOUT_POS } from "../../../../../models/sift/select/layout-pos";
import { Button } from "../../../../ui/button/button";
import { IoInformationCircleOutline } from "react-icons/io5";
import { NewPage } from "../../../../../utilities/new-page";
import { webPagePath } from "../../../../../models/web-page-path";
import { Endpaper2Response, EndpaperResponse } from "../../../../../models/api/back/create-order/endpaper";

type Props = {
  data: ItemSettingsDataEntity | null;
  callback: (param: PrintOrderRequestParam) => void,
  request: Required<PrintOrderRequestParam>,
  itemElements: PrintItemElementDataEntity[],
  additional: {
    tape: EndpaperResponse['additionalInfos'],
    mat: Endpaper2Response['additionalInfos'],
  }
}

type VisibleContainerProps = {
  hidden?: boolean,
  children?: React.ReactNode | React.ReactNode[],
}

export const VisibleSwitchContainer = ({ hidden, children }: VisibleContainerProps) => {
  if (hidden) {
    return  <></>;
  }
  return <>{children}</>;
}

export const PrintItemSetting = (props: Props) => {
  const {
    data,
    request,
    itemElements,
    additional,
    callback,
  } = props;

  const paperTypeList = useMemo(() => {
    const target = itemElements.find((v) => v.progFlg === 'paper');
    return target?.list.map((v) => ({ value: v.dataId, label: v.dataName })) ?? [];
  }, [itemElements]);

  const borderTypeList = useMemo(() => {
    const target = itemElements.find((v) => v.progFlg === 'edgeDtemp');
    return target?.list.map((v) => ({ value: v.dataId, label: v.dataName })) ?? [];
  }, [itemElements]);

  const laminateTypeList = useMemo(() => {
    const target = itemElements.find((v) => v.progFlg === 'laminate');
    return target?.list.map((v) => ({ value: v.dataId, label: v.dataName })) ?? [];
  }, [itemElements]);

  const tapeList = useMemo(() => {
    return additional.tape.map((v) => ({ value: v.additionalInfoId, label: v.additionalInfoName }))
  }, [additional.tape]);

  const matList = useMemo(() => {
    return additional.mat.map((v) => ({ value: v.additionalInfo2Id, label: v.additionalInfo2Name }))
  }, [additional.mat]);

  const layoutOptions = useMemo(() => {
    return data?.layout?.options?.map((v) => ({
      value: v.dataId,
      label: v.dataText,
    })) ?? [];
  }, [data]);

  const viewOther = useMemo(() => {
    const viewList = [
      !!layoutOptions.length,
      !!data?.space,
      !!data?.trimming,
      !!data?.printDate,
      !!data?.backprint,
    ];
    return viewList.some((v) => v);
  }, [layoutOptions, data]);

  const handlerClickImageInitialPlacement = useCallback(() => {
    NewPage.tab(webPagePath.imageInitialPlacement);
  }, []);

  const handlerClickDatePrintUsage = useCallback(() => {
    NewPage.tab(webPagePath.datePrintUsage);
  }, []);

  if (!data) return <></>;

  return (
    <>
      { paperTypeList.length || borderTypeList.length || laminateTypeList.length || tapeList.length || matList.length ?
        <div className="search_print_order">
          <div className="bottom_border_header search_print_order__header">
            部材・加工
          </div>
          <div className="search_print_order">
            {!!paperTypeList.length && (
              <PrintLabeledUI label="ペーパー種">
                {paperTypeList.length === 1 ?
                  (
                    <div>{paperTypeList[0]?.label ?? ''}</div>
                  ) : (
                    <Select
                      list={paperTypeList}
                      value={request.paperType}
                      addEmpty
                      addEmptyLabel=""
                      onChange={(v) => callback({ paperType: v.target.value })}
                    />
                  )}
              </PrintLabeledUI>
            )}
            {!!laminateTypeList.length && (
              <PrintLabeledUI label="ラミネート種">
                {laminateTypeList.length === 1 ? (
                  <div>{laminateTypeList[0]?.label ?? ''}</div>
                ) : (
                  <Select
                    list={laminateTypeList}
                    value={request.laminateType}
                    addEmpty
                    addEmptyLabel=""
                    onChange={(v) => callback({ laminateType: v.target.value })}
                  />
                )}
              </PrintLabeledUI>
            )}
            {!!borderTypeList.length && (
              <PrintLabeledUI label="フチ">
                {borderTypeList.length === 1 ? (
                  <div>{borderTypeList[0]?.label ?? ''}</div>
                ) : (
                  <Select
                    list={borderTypeList}
                    value={request.borderType}
                    addEmpty
                    addEmptyLabel=""
                    onChange={(v) => callback({ borderType: v.target.value })}
                  />
                )}
              </PrintLabeledUI>
            )}
            {!!tapeList.length && <PrintLabeledUI label="テープ色">
              {tapeList.length === 1 ? (
                <div>{tapeList[0]?.label ?? ''}</div>
              ) : (
                <Select
                  list={tapeList}
                  value={request.additionalInfo}
                  addEmpty
                  addEmptyLabel=""
                  onChange={(v) => callback({ additionalInfo: v.target.value })}
                />
              )}
            </PrintLabeledUI>}
            {!!matList.length && <PrintLabeledUI label="マット">
              {matList.length === 1 ? (
                <div>{matList[0]?.label ?? ''}</div>
              ) : (
                <Select
                  list={matList}
                  value={request.additionalInfo2}
                  addEmpty
                  addEmptyLabel=""
                  onChange={(v) => callback({ additionalInfo2: v.target.value })}
                />
              )}
            </PrintLabeledUI>}
          </div>
        </div> : <></>
      }
      <VisibleSwitchContainer hidden={!viewOther}>
        <div className="search_print_order">
          <div className="bottom_border_header search_print_order__header">
            レイアウト・仕上げ
          </div>
          <div className="search_print_order">
            <VisibleSwitchContainer hidden={!layoutOptions.length}>
              <PrintLabeledUI label="画像の初期配置方法">
                <Select
                  list={PRINT_LAYOUT_POS.map((v) => ({...v, value: `${v.value}`}))}
                  value={`${request.layoutInitPos}` || `${PRINT_LAYOUT_POS?.[0]?.value ?? ''}`}
                  onChange={(e) => callback({ layoutInitPos: Number(e.target.value) || 1 })}
                />
                <Button
                  className="annotation_button"
                  color="annotation"
                  icon={<IoInformationCircleOutline size="24px" />}
                  label="「外接」「内接」「ピクセル等倍」の違いについての解説"
                  onClick={handlerClickImageInitialPlacement}
                />
              </PrintLabeledUI>
            </VisibleSwitchContainer>
            <VisibleSwitchContainer hidden={!data.space}>
              <PrintLabeledUI label="レイアウト時の余白">
                <Checkbox
                  label="余白を許可する"
                  checked={!!request.layoutMargin}
                  onClick={() => callback({ layoutMargin: !request.layoutMargin })}
                  disabled={request.layoutInitPos !== 1}
                />
              </PrintLabeledUI>
            </VisibleSwitchContainer>
            <VisibleSwitchContainer hidden={!data.trimming}>
              <PrintLabeledUI label="トリミング">
                <Checkbox
                  label="トリミングをまかせる"
                  checked={!!request.trimming}
                  onClick={() => callback({ trimming: !request.trimming })}
                />
              </PrintLabeledUI>
            </VisibleSwitchContainer>
            <VisibleSwitchContainer hidden={!data.printDate}>
              <PrintLabeledUI label="日付プリント">
                <Checkbox
                  label="日付プリントをする"
                  checked={!!request.printDate}
                  onClick={() => callback({ printDate: !request.printDate })}
                />
                <Button
                  className="annotation_button"
                  color="annotation"
                  icon={<IoInformationCircleOutline size="24px" />}
                  label="日付プリントの仕様についてご確認ください"
                  onClick={handlerClickDatePrintUsage}
                />
              </PrintLabeledUI>
            </VisibleSwitchContainer>
            <VisibleSwitchContainer hidden={!data.backprint}>
              <PrintLabeledUI label="裏印字">
                <Checkbox
                  label="裏印字をする"
                  checked={!!request.backPrint}
                  onClick={() => callback({ backPrint: !request.backPrint })}
                />
              </PrintLabeledUI>
            </VisibleSwitchContainer>
          </div>
        </div>
      </VisibleSwitchContainer>
    </>
  )
}
